// Hooks TanStack Query de Pessoas (Parte 6.5; GIY-067) sobre o cliente tipado gerado do contrato. Chaves começam por
// `['people', 'network', …]`, `['people', 'tenant', tenantId, …]` ou `['people', 'me', …]` para o clear de cache na troca
// de contexto (regra 6 de 14.7). Mutações são `silent`: cada tela mostra o erro inline (ProblemAlert); as que exigem
// step-up (A.1: U — Matriz, exceção de transição, importação) são envolvidas por `useStepUpAction` na tela. Dados
// regulados levam `changeReason` e, nos `PUT`, `If-Match` com o ETag devolvido pelo servidor (412 sem ele). Leituras
// sensíveis (identificador fiscal, point-in-time — A.1: P) só rodam com um propósito declarado (`X-Purpose`).
//
// Listas devolvem arrays sem ETag: para editar um item, a tela lê o item (`useLicense`, `useCertification`,
// `useWorkSchedule`, `useAvailabilityDeclaration`, `usePersonLink`) e usa o `etag` dele.
import {
  type DeclaredPurpose,
  GiyusApiError,
  type GiyusClient,
  type components,
  formatPurposeHeader,
} from '@giyus/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../../../lib/api/apiClientContext';
import type { PeopleScope } from './scope';

type Schemas = components['schemas'];

// ---- Tipos do contrato ----

export type PersonSummary = Schemas['PeoplePersonSummaryResponse'];
export type PersonPage = Schemas['PeoplePersonPageResponse'];
export type PersonDetail = Schemas['PeoplePersonDetailResponse'];
export type PersonCreated = Schemas['PeoplePersonCreatedResponse'];
export type PersonSensitive = Schemas['PeoplePersonSensitiveResponse'];
export type PersonContact = Schemas['PeoplePersonContactResponse'];
export type ContactInput = Schemas['PeoplePersonContactRequest'];
export type CreatePersonInput = Schemas['PeopleCreatePersonRequest'];
export type UpdatePersonInput = Schemas['PeopleUpdatePersonRequest'];
export type ChangeReasonInput = Schemas['PeopleChangeReasonRequest'];
export type PersonLink = Schemas['PeoplePersonLinkResponse'];
export type LinkInput = Schemas['PeopleLinkRequest'];
export type UpdateLinkInput = Schemas['PeopleUpdateLinkRequest'];
export type EndLinkInput = Schemas['PeopleEndLinkRequest'];
export type TransitionException = Schemas['PeopleTransitionExceptionResponse'];
export type TransitionExceptionInput = Schemas['PeopleTransitionExceptionRequest'];
export type PeopleFunction = Schemas['PeopleFunctionResponse'];
export type LegalEntityRef = Schemas['PeopleLegalEntityRefResponse'];
export type MatrixVersion = Schemas['PeopleMatrixVersionResponse'];
export type MatrixVersionSummary = Schemas['PeopleMatrixVersionSummaryResponse'];
export type MatrixEntry = Schemas['PeopleMatrixEntryResponse'];
export type MatrixEntryInput = Schemas['PeopleMatrixEntryRequest'];
export type ProposeMatrixInput = Schemas['PeopleProposeMatrixRequest'];
export type CertificationType = Schemas['PeopleCertificationTypeResponse'];
export type ProfessionalBoard = Schemas['PeopleProfessionalRegistrationBoardResponse'];
export type SkillCatalogEntry = Schemas['PeopleSkillCatalogEntryResponse'];
export type License = Schemas['PeopleLicenseResponse'];
export type LicenseInput = Schemas['PeopleLicenseRequest'];
export type Certification = Schemas['PeopleCertificationResponse'];
export type CertificationInput = Schemas['PeopleCertificationRequest'];
export type PersonSkill = Schemas['PeoplePersonSkillResponse'];
export type PersonSkillInput = Schemas['PeoplePersonSkillRequest'];
export type ReplaceSkillsInput = Schemas['PeopleReplacePersonSkillsRequest'];
export type WorkSchedule = Schemas['PeopleWorkScheduleResponse'];
export type WorkScheduleInput = Schemas['PeopleWorkScheduleRequest'];
export type Availability = Schemas['PeopleAvailabilityResponse'];
export type AvailabilityInput = Schemas['PeopleAvailabilityRequest'];
export type TimeWindow = Schemas['PeopleTimeWindowResponse'];
export type TimeWindowInput = Schemas['PeopleTimeWindowRequest'];
export type Absence = Schemas['PeopleAbsenceResponse'];
export type AbsenceInput = Schemas['PeopleAbsenceRequest'];
export type AbsenceCreated = Schemas['PeopleAbsenceCreatedResponse'];
export type AbsenceImpact = Schemas['PeopleAbsenceImpactResponse'];
export type ImpactedAppointment = Schemas['PeopleImpactedAppointmentResponse'];
export type OnboardingCase = Schemas['PeopleOnboardingCaseResponse'];
export type OffboardingCase = Schemas['PeopleOffboardingCaseResponse'];
export type ChecklistItem = Schemas['PeopleChecklistItemResponse'];
export type ImportBatch = Schemas['PeopleImportBatchResponse'];
export type ImportBatchPage = Schemas['PeopleImportBatchPageResponse'];
export type ImportRow = Schemas['PeopleImportRowResponse'];
export type ImportRowError = Schemas['PeopleImportRowErrorResponse'];
export type ImportPeopleInput = Schemas['PeopleImportPeopleRequest'];
export type PointInTimeReport = Schemas['PeoplePointInTimeResponse'];
export type PointInTimePerson = Schemas['PointInTimePerson'];
export type PointInTimeLink = Schemas['PointInTimeLink'];
export type PointInTimeCertification = Schemas['PointInTimeCertification'];
export type TwoKeyApproval = Schemas['AuthorizationTwoKeyApprovalResponse'];
export type LinkType = Schemas['LinkType'];
export type PersonStatus = Schemas['PersonStatus'];
export type FunctionStatus = Schemas['FunctionStatus'];

// ---- Enumerações de texto do contrato (o servidor as devolve como `string`) ----

export const LINK_TYPES: readonly LinkType[] = [
  'Socio',
  'CLT',
  'PJ',
  'CooperadoCPESQ',
  'Estagiario',
];
export const PERSON_STATUSES: readonly PersonStatus[] = ['Active', 'Inactive'];
export const LINK_STATUSES = ['Scheduled', 'Active', 'Expired', 'Ended'] as const;
export type LinkStatus = (typeof LINK_STATUSES)[number];
export const CLASSIFICATION_STATUSES = ['Valid', 'PendingOpinion', 'Invalid'] as const;
export type ClassificationStatus = (typeof CLASSIFICATION_STATUSES)[number];
export const VISIBILITY_REASONS = ['Link', 'Assignment', 'Delegation'] as const;
export type VisibilityReason = (typeof VISIBILITY_REASONS)[number];
export const FUNCTION_CLASSES = [
  'Clinical',
  'Operational',
  'Technical',
  'Management',
  'Quality',
  'Support',
] as const;
export type FunctionClass = (typeof FUNCTION_CLASSES)[number];
export const FUNCTION_STATUSES: readonly FunctionStatus[] = ['Active', 'Retired'];
export const MATRIX_INDICATIONS = ['Indicated', 'CaseByCase', 'NotAllowed'] as const;
export type MatrixIndication = (typeof MATRIX_INDICATIONS)[number];
export const MATRIX_STATUSES = [
  'PendingSecondKey',
  'Published',
  'Superseded',
  'Cancelled',
] as const;
export type MatrixStatus = (typeof MATRIX_STATUSES)[number];
export const CONTACT_KINDS = ['Email', 'Phone', 'WhatsApp'] as const;
export type ContactKind = (typeof CONTACT_KINDS)[number];
/** Dias da semana da escala/disponibilidade, na ordem canônica do servidor (chaves minúsculas de `weekly`). */
export const WEEK_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];
export const AVAILABILITY_KINDS = ['Weekly', 'Dated'] as const;
export type AvailabilityKind = (typeof AVAILABILITY_KINDS)[number];
export const ABSENCE_KINDS = [
  'Vacation',
  'Leave',
  'Withdrawal',
  'Training',
  'Travel',
  'Other',
] as const;
export type AbsenceKind = (typeof ABSENCE_KINDS)[number];
export const CREDENTIAL_STATUSES = ['Valid', 'Expiring', 'Expired', 'Revoked'] as const;
export type CredentialStatus = (typeof CREDENTIAL_STATUSES)[number];
export const CERTIFICATION_ORIGINS = ['Manual', 'Lms'] as const;
export type CertificationOrigin = (typeof CERTIFICATION_ORIGINS)[number];
export const CERTIFICATION_SCOPES = ['Network', 'Study'] as const;
export type CertificationScope = (typeof CERTIFICATION_SCOPES)[number];
export const CHECKLIST_STATUSES = ['Pending', 'Done', 'NotApplicable'] as const;
export type ChecklistStatus = (typeof CHECKLIST_STATUSES)[number];
export const ONBOARDING_STATUSES = ['InProgress', 'ReadyToOperate'] as const;
export const OFFBOARDING_STATUSES = ['InProgress', 'Completed'] as const;
/** Item do offboarding que grava `AccessRevokedAt`: só o sistema o conclui (`authz.access.revoked`; OPEN-117) — a marcação manual responde `422 PPL_OFFBOARDING_ITEM_AUTOMATIC`. */
export const ACCESS_REVOCATION_ITEM = 'AccessRevocationConfirmed';
export const TRANSITION_EXCEPTION_STATUSES = [
  'PendingSecondKey',
  'Approved',
  'Consumed',
  'Cancelled',
] as const;
export type TransitionExceptionStatus = (typeof TRANSITION_EXCEPTION_STATUSES)[number];
export const IMPORT_MODES = ['Atomic', 'Partial'] as const;
export type ImportMode = (typeof IMPORT_MODES)[number];
export const IMPORT_STATUSES = [
  'Processing',
  'Applied',
  'PartiallyApplied',
  'Rejected',
  'Failed',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];
export const IMPORT_ROW_OUTCOMES = ['Applied', 'Rejected', 'NotApplied', 'NotProcessed'] as const;
export type ImportRowOutcome = (typeof IMPORT_ROW_OUTCOMES)[number];
/** Colunas do CSV de importação (cabeçalho em `camelCase`; as obrigatórias vêm primeiro). */
export const IMPORT_REQUIRED_COLUMNS = [
  'givenName',
  'familyName',
  'federalCode',
  'birthDate',
  'linkType',
  'functionCode',
  'effectiveFrom',
] as const;
export const IMPORT_OPTIONAL_COLUMNS = [
  'socialName',
  'country',
  'preferredLocale',
  'email',
  'phone',
  'effectiveTo',
  'pjCnpj',
  'cooperativeMemberNumber',
  'primarySiteCode',
  'classificationJustification',
] as const;

// ---- Chaves de cache ----

export const peopleKey = ['people'] as const;
export const networkKey = ['people', 'network'] as const;
export const tenantKey = (tenantId: string) => ['people', 'tenant', tenantId] as const;
export const meKey = ['people', 'me'] as const;

/** Raiz das chaves de um escopo: rede ou tenant. */
export function scopeKey(scope: PeopleScope): readonly string[] {
  return scope.kind === 'network' ? networkKey : tenantKey(scope.tenantId);
}

/** Recurso com versão: o `ETag` do cabeçalho é o contrato do `If-Match` (Parte 13). */
export interface Versioned<T> {
  readonly data: T;
  readonly etag: string | null;
}

function etagOf(response: Response): string | null {
  return response.headers.get('ETag');
}

/** Comandos idempotentes (Parte 13.1): o contrato exige a chave; uma nova por tentativa. */
function idempotency(): { 'Idempotency-Key': string } {
  return { 'Idempotency-Key': crypto.randomUUID() };
}

/** A leitura não trouxe `ETag`: sem versão não há `PUT` (o `If-Match` nunca vai vazio nem como curinga). */
export class MissingEtagError extends Error {
  constructor() {
    super('The resource version (ETag) is unknown: re-read the resource before saving.');
    this.name = 'MissingEtagError';
  }
}

/**
 * `If-Match` do `PUT` (Parte 13.1; REQ-PLT-013). Sem `ETag` lança `MissingEtagError` e nada é enviado: o curinga `*` casaria com
 * QUALQUER versão no servidor e anularia o controle de concorrência de dado regulado — as telas desabilitam o envio antes.
 */
function ifMatch(etag: string | null): { 'If-Match': string } {
  if (etag === null || etag === '') {
    throw new MissingEtagError();
  }
  return { 'If-Match': etag };
}

function purposeHeader(purpose: DeclaredPurpose): { 'X-Purpose': string } {
  return { 'X-Purpose': formatPurposeHeader(purpose) };
}

function required<T>(data: T | undefined): T {
  if (data === undefined) {
    throw new Error('Empty response');
  }
  return data;
}

/** Despacha para a rota de rede ou para o espelho do tenant (os caminhos tipados do contrato são literais). */
function viaScope<N, T>(
  scope: PeopleScope,
  network: () => N,
  tenant: (tenantId: string) => T,
): N | T {
  return scope.kind === 'network' ? network() : tenant(scope.tenantId);
}

/** Invalida todo o cache de pessoas (a mutação pode mudar a lista, a ficha, os vínculos e "Meu cadastro"). */
function useInvalidate() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: peopleKey });
  };
}

/** `404` de recurso que ainda não existe (onboarding não iniciado, offboarding de vínculo ativo): vira `null`, não erro. */
function isNotFound(error: unknown): boolean {
  return error instanceof GiyusApiError && error.status === 404;
}

// ---- Pessoas (REQ-PPL-001, 015, 016, 023) ----

export interface PersonFilters {
  readonly q?: string | undefined;
  readonly status?: PersonStatus | undefined;
  readonly linkType?: LinkType | undefined;
  /** Código da função (`FunctionDefinition.Code`). */
  readonly functionCode?: string | undefined;
  readonly legalEntityId?: string | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
}

function personQuery(filters: PersonFilters) {
  return {
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.linkType ? { linkType: filters.linkType } : {}),
    ...(filters.functionCode ? { function: filters.functionCode } : {}),
    ...(filters.legalEntityId ? { legalEntityId: filters.legalEntityId } : {}),
    ...(filters.page ? { page: filters.page } : {}),
    ...(filters.pageSize ? { pageSize: filters.pageSize } : {}),
  };
}

export function usePeopleList(scope: PeopleScope, filters: PersonFilters) {
  const client = useApiClient();
  const query = personQuery(filters);
  return useQuery({
    queryKey: [...scopeKey(scope), 'list', filters],
    queryFn: async (): Promise<PersonPage> => {
      const { data } = await viaScope(
        scope,
        () => client.GET('/api/v1/network/people', { params: { query } }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people', {
            params: { path: { tenantId }, query },
          }),
      );
      return required(data);
    },
    meta: { silent: true },
  });
}

export function usePerson(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId],
    queryFn: async (): Promise<Versioned<PersonDetail>> => {
      const { data, response } = await viaScope(
        scope,
        () => client.GET('/api/v1/network/people/{personId}', { params: { path: { personId } } }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}', {
            params: { path: { tenantId, personId } },
          }),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(personId),
    meta: { silent: true },
  });
}

export function useCreatePerson(scope: PeopleScope) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: CreatePersonInput): Promise<PersonCreated> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people', { params: { header: idempotency() }, body: input }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people', {
            params: { path: { tenantId }, header: idempotency() },
            body: input,
          }),
      );
      return required(data);
    },
    onSuccess: invalidate,
    // As variáveis levam CPF e nascimento (cofre): a mutação não fica no cache além do uso (a tela chama `reset()` ao salvar).
    gcTime: 0,
    meta: { silent: true },
  });
}

export interface UpdatePersonArgs {
  readonly etag: string | null;
  readonly input: UpdatePersonInput;
}

async function putPerson(
  client: GiyusClient,
  scope: PeopleScope,
  personId: string,
  args: UpdatePersonArgs,
): Promise<Versioned<PersonDetail>> {
  const { data, response } = await viaScope(
    scope,
    () =>
      client.PUT('/api/v1/network/people/{personId}', {
        params: { path: { personId }, header: ifMatch(args.etag) },
        body: args.input,
      }),
    (tenantId) =>
      client.PUT('/api/v1/tenants/{tenantId}/people/{personId}', {
        params: { path: { tenantId, personId }, header: ifMatch(args.etag) },
        body: args.input,
      }),
  );
  return { data: required(data), etag: etagOf(response) };
}

/** `PUT` da ficha (If-Match + `changeReason`): para uma edição parcial, parta de `personToUpdateInput`. */
export function useUpdatePerson(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (args: UpdatePersonArgs): Promise<Versioned<PersonDetail>> =>
      putPerson(client, scope, personId, args),
    onSuccess: invalidate,
    gcTime: 0,
    meta: { silent: true },
  });
}

function asPersonStatus(value: string): PersonStatus {
  return value === 'Inactive' ? 'Inactive' : 'Active';
}

/**
 * Corpo do `PUT` da pessoa a partir da ficha atual (os campos civis seguem como estão): o `PUT` substitui o cadastro,
 * então uma edição parcial (só contatos, só o status) parte daqui e troca só o que mudou. O identificador fiscal e o
 * nascimento só viajam juntos e nunca por aqui (cofre — regra 11 da Parte 0.3): a ficha não os devolve em claro.
 */
export function personToUpdateInput(
  person: PersonDetail,
  changeReason: ChangeReasonInput,
  overrides: Partial<UpdatePersonInput> = {},
): UpdatePersonInput {
  return {
    givenName: person.givenName,
    familyName: person.familyName,
    socialName: person.socialName,
    preferredLocale: person.preferredLocale,
    photoDocumentId: person.photoDocumentId,
    status: asPersonStatus(person.status),
    changeReason,
    ...overrides,
  };
}

export interface UpdatePersonContactsArgs {
  readonly person: PersonDetail;
  readonly etag: string | null;
  readonly contacts: readonly ContactInput[];
  readonly changeReason: ChangeReasonInput;
}

/** Troca só os contatos da pessoa (`PUT` da ficha com os demais campos como estão). */
export function useUpdatePersonContacts(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (args: UpdatePersonContactsArgs): Promise<Versioned<PersonDetail>> =>
      putPerson(client, scope, personId, {
        etag: args.etag,
        input: personToUpdateInput(args.person, args.changeReason, {
          contacts: [...args.contacts],
        }),
      }),
    onSuccess: invalidate,
    gcTime: 0,
    meta: { silent: true },
  });
}

/** Identificador fiscal e nascimento (cofre): só com `person.sensitive.read` + propósito declarado; auditado. */
export function usePersonSensitive(
  scope: PeopleScope,
  personId: string,
  purpose: DeclaredPurpose | null,
) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'sensitive', purpose?.code ?? null],
    queryFn: async (): Promise<PersonSensitive> => {
      if (!purpose) {
        throw new Error('the sensitive read needs a declared purpose');
      }
      const header = purposeHeader(purpose);
      const { data } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/sensitive', {
            params: { path: { personId }, header },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/sensitive', {
            params: { path: { tenantId, personId }, header },
          }),
      );
      return required(data);
    },
    enabled: Boolean(personId) && purpose !== null,
    // Dado sensível: não fica em cache além do uso.
    gcTime: 0,
    staleTime: 0,
    meta: { silent: true },
  });
}

// ---- Vínculos (REQ-PPL-002..005, 014, 017) ----

export function usePersonLinks(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'links'],
    queryFn: async (): Promise<PersonLink[]> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/links', {
            params: { path: { personId } },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/links', {
            params: { path: { tenantId, personId } },
          }),
      );
      return data ?? [];
    },
    enabled: Boolean(personId),
    meta: { silent: true },
  });
}

export function usePersonLink(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'links', linkId],
    queryFn: async (): Promise<Versioned<PersonLink>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/links/{linkId}', {
            params: { path: { personId, linkId } },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}', {
            params: { path: { tenantId, personId, linkId } },
          }),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(personId) && Boolean(linkId),
    // O diálogo de edição só monta o formulário com uma leitura feita DEPOIS de abrir: sempre relê (nunca usa o cache de uma abertura anterior).
    refetchOnMount: 'always',
    meta: { silent: true },
  });
}

/** `423 PPL_LINK_NOT_ALLOWED`/`PPL_BLOCKED_TRANSITION` são travas (nunca avisos): a tela as mostra e não repete a ação. */
export function useCreateLink(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: LinkInput): Promise<Versioned<PersonLink>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/links', {
            params: { path: { personId }, header: idempotency() },
            body: input,
          }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people/{personId}/links', {
            params: { path: { tenantId, personId }, header: idempotency() },
            body: input,
          }),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface EndLinkArgs {
  readonly linkId: string;
  /** Razão obrigatória (≤ 400): vira `EndReason` e a trilha. */
  readonly reason: string;
  /** Data de término (padrão: hoje, no servidor). */
  readonly endDate?: string | null | undefined;
}

export function useEndLink(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: EndLinkArgs): Promise<PersonLink> => {
      const body: EndLinkInput = { reason: args.reason, endDate: args.endDate ?? null };
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/links/{linkId}/end', {
            params: { path: { personId, linkId: args.linkId }, header: idempotency() },
            body,
          }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/end', {
            params: { path: { tenantId, personId, linkId: args.linkId }, header: idempotency() },
            body,
          }),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface UpdateLinkArgs {
  readonly etag: string | null;
  readonly input: UpdateLinkInput;
}

export function useUpdateLink(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: UpdateLinkArgs): Promise<Versioned<PersonLink>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.PUT('/api/v1/network/people/{personId}/links/{linkId}', {
            params: { path: { personId, linkId }, header: ifMatch(args.etag) },
            body: args.input,
          }),
        (tenantId) =>
          client.PUT('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}', {
            params: { path: { tenantId, personId, linkId }, header: ifMatch(args.etag) },
            body: args.input,
          }),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Exceções de transição CLT → flexível (REQ-PPL-005; só rede; duas chaves RH + Jurídico) ----

export function useTransitionExceptions(personId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'person', personId, 'transition-exceptions'],
    queryFn: async (): Promise<TransitionException[]> => {
      const { data } = await client.GET(
        '/api/v1/network/people/{personId}/links/transition-exceptions',
        { params: { path: { personId } } },
      );
      return data ?? [];
    },
    enabled: Boolean(personId),
    meta: { silent: true },
  });
}

export function useTransitionException(personId: string, exceptionId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'person', personId, 'transition-exceptions', exceptionId],
    queryFn: async (): Promise<Versioned<TransitionException>> => {
      const { data, response } = await client.GET(
        '/api/v1/network/people/{personId}/links/transition-exceptions/{exceptionId}',
        { params: { path: { personId, exceptionId } } },
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(personId) && Boolean(exceptionId),
    meta: { silent: true },
  });
}

/** Primeira chave (RH, `person.link.exception.request`, step-up): `202` `PendingSecondKey`; a segunda chave é o `useSecondKey`. */
export function useRequestTransitionException(personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: TransitionExceptionInput): Promise<TransitionException> => {
      const { data } = await client.POST(
        '/api/v1/network/people/{personId}/links/transition-exceptions',
        { params: { path: { personId }, header: idempotency() }, body: input },
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Pessoas jurídicas para seletores (REQ-PPL-002; OPEN-095) ----

/** Holding e centros (rede) ou só o próprio centro (tenant): id, tipo, código e nome — alimenta os seletores de pessoa jurídica. */
export function useLegalEntities(scope: PeopleScope) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'legal-entities'],
    queryFn: async (): Promise<LegalEntityRef[]> => {
      const { data } = await viaScope(
        scope,
        () => client.GET('/api/v1/network/people/legal-entities'),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/legal-entities', {
            params: { path: { tenantId } },
          }),
      );
      return data ?? [];
    },
    meta: { silent: true },
  });
}

// ---- Funções e Matriz de Enquadramento (REQ-PPL-003) ----

/** Catálogo de funções (padrão: só as ativas; `Retired` lista as aposentadas). */
export function useFunctions(scope: PeopleScope, status?: FunctionStatus) {
  const client = useApiClient();
  const query = status ? { status } : {};
  return useQuery({
    queryKey: [...scopeKey(scope), 'functions', status ?? null],
    queryFn: async (): Promise<PeopleFunction[]> => {
      const { data } = await viaScope(
        scope,
        () => client.GET('/api/v1/network/people/functions', { params: { query } }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/functions', {
            params: { path: { tenantId }, query },
          }),
      );
      return data ?? [];
    },
    meta: { silent: true },
  });
}

/** Matriz vigente (rede: `people.matrix.read`; tenant: `person.link.read`); o `ETag` é o `If-Match` da proposta. */
export function useCurrentMatrix(scope: PeopleScope) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'matrix', 'current'],
    queryFn: async (): Promise<Versioned<MatrixVersion>> => {
      const { data, response } = await viaScope(
        scope,
        () => client.GET('/api/v1/network/people/function-link-matrix'),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/function-link-matrix', {
            params: { path: { tenantId } },
          }),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    meta: { silent: true },
  });
}

export function useMatrixVersions() {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'matrix', 'versions'],
    queryFn: async (): Promise<MatrixVersionSummary[]> => {
      const { data } = await client.GET('/api/v1/network/people/function-link-matrix/versions');
      return data ?? [];
    },
    meta: { silent: true },
  });
}

export function useMatrixVersion(versionId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'matrix', 'versions', versionId],
    queryFn: async (): Promise<Versioned<MatrixVersion>> => {
      const { data, response } = await client.GET(
        '/api/v1/network/people/function-link-matrix/versions/{versionId}',
        { params: { path: { versionId } } },
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(versionId),
    meta: { silent: true },
  });
}

export interface ProposeMatrixArgs {
  /** ETag da Matriz vigente (`useCurrentMatrix`). */
  readonly etag: string | null;
  readonly input: ProposeMatrixInput;
}

/** Primeira chave (RH, `people.matrix.manage`, step-up): `202` `PendingSecondKey` com o `twoKeyApprovalId` da segunda chave. */
export function useProposeMatrix() {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: ProposeMatrixArgs): Promise<MatrixVersion> => {
      const { data } = await client.PUT('/api/v1/network/people/function-link-matrix', {
        params: { header: ifMatch(args.etag) },
        body: args.input,
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface SecondKeyArgs {
  readonly approvalId: string;
  readonly comment: string | null;
}

/**
 * Segunda chave pela rota genérica de duas chaves (`POST /authz/two-key/{approvalId}/second-key`, step-up): serve à
 * Matriz (`MatrixVersion.twoKeyApprovalId`, Jurídico) e à exceção de transição (`TransitionException.twoKeyApprovalId`).
 * Quem deu a primeira chave nunca dá a segunda (`422 AUTHZ_TWO_KEY_SAME_ACTOR`) e o papel precisa ser outro
 * (`422 AUTHZ_TWO_KEY_DISTINCT_ROLE_REQUIRED`).
 */
export function useSecondKey() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: SecondKeyArgs): Promise<TwoKeyApproval> => {
      const { data } = await client.POST('/api/v1/authz/two-key/{approvalId}/second-key', {
        params: { path: { approvalId: args.approvalId }, header: idempotency() },
        body: { comment: args.comment },
      });
      return required(data);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: peopleKey }),
        queryClient.invalidateQueries({ queryKey: ['authz'] }),
      ]);
    },
    meta: { silent: true },
  });
}

// ---- Catálogos de rede (REQ-PPL-009, 011, 024): o tenant lê os espelhos `/tenants/{t}/people/*` ----

export function useCertificationTypes(scope: PeopleScope) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'catalog', 'certification-types'],
    queryFn: async (): Promise<CertificationType[]> => {
      const { data } = await viaScope(
        scope,
        () => client.GET('/api/v1/network/people/certification-types'),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/certification-types', {
            params: { path: { tenantId } },
          }),
      );
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    meta: { silent: true },
  });
}

export function useBoards(scope: PeopleScope) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'catalog', 'professional-registration-boards'],
    queryFn: async (): Promise<ProfessionalBoard[]> => {
      const { data } = await viaScope(
        scope,
        () => client.GET('/api/v1/network/people/professional-registration-boards'),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/professional-registration-boards', {
            params: { path: { tenantId } },
          }),
      );
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    meta: { silent: true },
  });
}

export function useSkillCatalog(scope: PeopleScope) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'catalog', 'skills'],
    queryFn: async (): Promise<SkillCatalogEntry[]> => {
      const { data } = await viaScope(
        scope,
        () => client.GET('/api/v1/network/people/skill-catalog'),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/skill-catalog', {
            params: { path: { tenantId } },
          }),
      );
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    meta: { silent: true },
  });
}

// ---- Licenças profissionais (REQ-PPL-008) ----

export function useLicenses(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'licenses'],
    queryFn: async (): Promise<License[]> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/licenses', {
            params: { path: { personId } },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/licenses', {
            params: { path: { tenantId, personId } },
          }),
      );
      return data ?? [];
    },
    enabled: Boolean(personId),
    meta: { silent: true },
  });
}

export function useLicense(scope: PeopleScope, personId: string, licenseId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'licenses', licenseId],
    queryFn: async (): Promise<Versioned<License>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/licenses/{licenseId}', {
            params: { path: { personId, licenseId } },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/licenses/{licenseId}', {
            params: { path: { tenantId, personId, licenseId } },
          }),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(personId) && Boolean(licenseId),
    // O diálogo de edição só monta o formulário com uma leitura feita DEPOIS de abrir: sempre relê (nunca usa o cache de uma abertura anterior).
    refetchOnMount: 'always',
    meta: { silent: true },
  });
}

export function useCreateLicense(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: LicenseInput): Promise<License> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/licenses', {
            params: { path: { personId }, header: idempotency() },
            body: input,
          }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people/{personId}/licenses', {
            params: { path: { tenantId, personId }, header: idempotency() },
            body: input,
          }),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface UpdateLicenseArgs {
  readonly licenseId: string;
  readonly etag: string | null;
  /** `changeReason` obrigatório no `PUT` (dado regulado). */
  readonly input: LicenseInput;
}

export function useUpdateLicense(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: UpdateLicenseArgs): Promise<Versioned<License>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.PUT('/api/v1/network/people/{personId}/licenses/{licenseId}', {
            params: { path: { personId, licenseId: args.licenseId }, header: ifMatch(args.etag) },
            body: args.input,
          }),
        (tenantId) =>
          client.PUT('/api/v1/tenants/{tenantId}/people/{personId}/licenses/{licenseId}', {
            params: {
              path: { tenantId, personId, licenseId: args.licenseId },
              header: ifMatch(args.etag),
            },
            body: args.input,
          }),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Certificações (REQ-PPL-009, 010; verificação por segunda pessoa) ----

export function useCertifications(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'certifications'],
    queryFn: async (): Promise<Certification[]> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/certifications', {
            params: { path: { personId } },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/certifications', {
            params: { path: { tenantId, personId } },
          }),
      );
      return data ?? [];
    },
    enabled: Boolean(personId),
    meta: { silent: true },
  });
}

export function useCertification(scope: PeopleScope, personId: string, certificationId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'certifications', certificationId],
    queryFn: async (): Promise<Versioned<Certification>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/certifications/{certificationId}', {
            params: { path: { personId, certificationId } },
          }),
        (tenantId) =>
          client.GET(
            '/api/v1/tenants/{tenantId}/people/{personId}/certifications/{certificationId}',
            { params: { path: { tenantId, personId, certificationId } } },
          ),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(personId) && Boolean(certificationId),
    // O diálogo de edição só monta o formulário com uma leitura feita DEPOIS de abrir: sempre relê (nunca usa o cache de uma abertura anterior).
    refetchOnMount: 'always',
    meta: { silent: true },
  });
}

export function useCreateCertification(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: CertificationInput): Promise<Certification> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/certifications', {
            params: { path: { personId }, header: idempotency() },
            body: input,
          }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people/{personId}/certifications', {
            params: { path: { tenantId, personId }, header: idempotency() },
            body: input,
          }),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface UpdateCertificationArgs {
  readonly certificationId: string;
  readonly etag: string | null;
  /** O tipo é imutável no `PUT`; `changeReason` obrigatório (dado regulado). */
  readonly input: CertificationInput;
}

export function useUpdateCertification(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: UpdateCertificationArgs): Promise<Versioned<Certification>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.PUT('/api/v1/network/people/{personId}/certifications/{certificationId}', {
            params: {
              path: { personId, certificationId: args.certificationId },
              header: ifMatch(args.etag),
            },
            body: args.input,
          }),
        (tenantId) =>
          client.PUT(
            '/api/v1/tenants/{tenantId}/people/{personId}/certifications/{certificationId}',
            {
              params: {
                path: { tenantId, personId, certificationId: args.certificationId },
                header: ifMatch(args.etag),
              },
              body: args.input,
            },
          ),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface VerifyCertificationArgs {
  readonly certificationId: string;
  /** ETag da leitura que o verificador viu (`If-Match` obrigatório; OPEN-099): uma edição depois dela responde `412`. */
  readonly etag: string | null;
}

/** Verificação por segunda pessoa (`person.credential.verify`): quem registrou nunca verifica (`422 AUTHZ_SOD_VIOLATION`). */
export function useVerifyCertification(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: VerifyCertificationArgs): Promise<Certification> => {
      const { certificationId } = args;
      const header = { ...idempotency(), ...ifMatch(args.etag) };
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/certifications/{certificationId}/verify', {
            params: { path: { personId, certificationId }, header },
          }),
        (tenantId) =>
          client.POST(
            '/api/v1/tenants/{tenantId}/people/{personId}/certifications/{certificationId}/verify',
            { params: { path: { tenantId, personId, certificationId }, header } },
          ),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface RevokeCertificationArgs {
  readonly certificationId: string;
  readonly etag: string | null;
  /** Razão obrigatória (a revogação é irreversível e auditada). */
  readonly changeReason: ChangeReasonInput;
}

/**
 * Revogação por Qualidade (`person.credential.revoke`; step-up; OPEN-099): `If-Match` da versão vista e razão obrigatória. A certificação revogada
 * nunca volta a valer (`409 PPL_CERTIFICATION_REVOKED` ao tentar de novo).
 */
export function useRevokeCertification(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: RevokeCertificationArgs): Promise<Certification> => {
      const { certificationId } = args;
      const header = ifMatch(args.etag);
      const body = { changeReason: args.changeReason };
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/certifications/{certificationId}/revoke', {
            params: { path: { personId, certificationId }, header },
            body,
          }),
        (tenantId) =>
          client.POST(
            '/api/v1/tenants/{tenantId}/people/{personId}/certifications/{certificationId}/revoke',
            { params: { path: { tenantId, personId, certificationId }, header }, body },
          ),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Capacidades (REQ-PPL-011): o `PUT` faz upsert por código e nunca remove ----

export function usePersonSkills(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'skills'],
    queryFn: async (): Promise<PersonSkill[]> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/skills', {
            params: { path: { personId } },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/skills', {
            params: { path: { tenantId, personId } },
          }),
      );
      return data ?? [];
    },
    enabled: Boolean(personId),
    meta: { silent: true },
  });
}

export function useReplaceSkills(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: ReplaceSkillsInput): Promise<PersonSkill[]> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.PUT('/api/v1/network/people/{personId}/skills', {
            params: { path: { personId } },
            body: input,
          }),
        (tenantId) =>
          client.PUT('/api/v1/tenants/{tenantId}/people/{personId}/skills', {
            params: { path: { tenantId, personId } },
            body: input,
          }),
      );
      return data ?? [];
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Escala (vínculos fixos — REQ-PPL-006) ----

export function useWorkSchedules(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'links', linkId, 'work-schedules'],
    queryFn: async (): Promise<WorkSchedule[]> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/links/{linkId}/work-schedules', {
            params: { path: { personId, linkId } },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/work-schedules', {
            params: { path: { tenantId, personId, linkId } },
          }),
      );
      return data ?? [];
    },
    enabled: Boolean(personId) && Boolean(linkId),
    meta: { silent: true },
  });
}

export function useWorkSchedule(
  scope: PeopleScope,
  personId: string,
  linkId: string,
  scheduleId: string,
) {
  const client = useApiClient();
  return useQuery({
    queryKey: [
      ...scopeKey(scope),
      'person',
      personId,
      'links',
      linkId,
      'work-schedules',
      scheduleId,
    ],
    queryFn: async (): Promise<Versioned<WorkSchedule>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.GET(
            '/api/v1/network/people/{personId}/links/{linkId}/work-schedules/{scheduleId}',
            { params: { path: { personId, linkId, scheduleId } } },
          ),
        (tenantId) =>
          client.GET(
            '/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/work-schedules/{scheduleId}',
            { params: { path: { tenantId, personId, linkId, scheduleId } } },
          ),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(personId) && Boolean(linkId) && Boolean(scheduleId),
    meta: { silent: true },
  });
}

/** Vínculo flexível → `422 PPL_NO_WORK_SCHEDULE_FOR_FLEXIBLE_LINK` (a tela nem oferece a escala: `scheduleModeFor`). */
export function useCreateWorkSchedule(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: WorkScheduleInput): Promise<Versioned<WorkSchedule>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/links/{linkId}/work-schedules', {
            params: { path: { personId, linkId }, header: idempotency() },
            body: input,
          }),
        (tenantId) =>
          client.POST(
            '/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/work-schedules',
            {
              params: { path: { tenantId, personId, linkId }, header: idempotency() },
              body: input,
            },
          ),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface UpdateWorkScheduleArgs {
  readonly scheduleId: string;
  readonly etag: string | null;
  /** O início da vigência é imutável (nova vigência = nova escala); `changeReason` obrigatório. */
  readonly input: WorkScheduleInput;
}

export function useUpdateWorkSchedule(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: UpdateWorkScheduleArgs): Promise<Versioned<WorkSchedule>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.PUT(
            '/api/v1/network/people/{personId}/links/{linkId}/work-schedules/{scheduleId}',
            {
              params: {
                path: { personId, linkId, scheduleId: args.scheduleId },
                header: ifMatch(args.etag),
              },
              body: args.input,
            },
          ),
        (tenantId) =>
          client.PUT(
            '/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/work-schedules/{scheduleId}',
            {
              params: {
                path: { tenantId, personId, linkId, scheduleId: args.scheduleId },
                header: ifMatch(args.etag),
              },
              body: args.input,
            },
          ),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Disponibilidade declarada (REQ-PPL-007) ----

export function useAvailability(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'links', linkId, 'availability'],
    queryFn: async (): Promise<Availability[]> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/links/{linkId}/availability', {
            params: { path: { personId, linkId } },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/availability', {
            params: { path: { tenantId, personId, linkId } },
          }),
      );
      return data ?? [];
    },
    enabled: Boolean(personId) && Boolean(linkId),
    meta: { silent: true },
  });
}

export function useAvailabilityDeclaration(
  scope: PeopleScope,
  personId: string,
  linkId: string,
  declarationId: string,
) {
  const client = useApiClient();
  return useQuery({
    queryKey: [
      ...scopeKey(scope),
      'person',
      personId,
      'links',
      linkId,
      'availability',
      declarationId,
    ],
    queryFn: async (): Promise<Versioned<Availability>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.GET(
            '/api/v1/network/people/{personId}/links/{linkId}/availability/{declarationId}',
            { params: { path: { personId, linkId, declarationId } } },
          ),
        (tenantId) =>
          client.GET(
            '/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/availability/{declarationId}',
            { params: { path: { tenantId, personId, linkId, declarationId } } },
          ),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(personId) && Boolean(linkId) && Boolean(declarationId),
    meta: { silent: true },
  });
}

export function useCreateAvailability(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: AvailabilityInput): Promise<Versioned<Availability>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/links/{linkId}/availability', {
            params: { path: { personId, linkId }, header: idempotency() },
            body: input,
          }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/availability', {
            params: { path: { tenantId, personId, linkId }, header: idempotency() },
            body: input,
          }),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface UpdateAvailabilityArgs {
  readonly declarationId: string;
  readonly etag: string | null;
  /** O tipo (`kind`) é imutável; `changeReason` obrigatório quando quem edita é o coordenador. */
  readonly input: AvailabilityInput;
}

export function useUpdateAvailability(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: UpdateAvailabilityArgs): Promise<Versioned<Availability>> => {
      const { data, response } = await viaScope(
        scope,
        () =>
          client.PUT(
            '/api/v1/network/people/{personId}/links/{linkId}/availability/{declarationId}',
            {
              params: {
                path: { personId, linkId, declarationId: args.declarationId },
                header: ifMatch(args.etag),
              },
              body: args.input,
            },
          ),
        (tenantId) =>
          client.PUT(
            '/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/availability/{declarationId}',
            {
              params: {
                path: { tenantId, personId, linkId, declarationId: args.declarationId },
                header: ifMatch(args.etag),
              },
              body: args.input,
            },
          ),
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Ausências (REQ-PPL-012): a prévia de impacto vem do seam do SCH (`schedulingAvailable=false` até o M11) ----

export interface AbsenceRange {
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

function absenceQuery(range: AbsenceRange) {
  return {
    ...(range.from ? { from: range.from } : {}),
    ...(range.to ? { to: range.to } : {}),
  };
}

export function useAbsences(scope: PeopleScope, personId: string, range: AbsenceRange = {}) {
  const client = useApiClient();
  const query = absenceQuery(range);
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'absences', range],
    queryFn: async (): Promise<Absence[]> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.GET('/api/v1/network/people/{personId}/absences', {
            params: { path: { personId }, query },
          }),
        (tenantId) =>
          client.GET('/api/v1/tenants/{tenantId}/people/{personId}/absences', {
            params: { path: { tenantId, personId }, query },
          }),
      );
      return data ?? [];
    },
    enabled: Boolean(personId),
    meta: { silent: true },
  });
}

/** Prévia de impacto sem gravar (`POST …/absences/preview-impact`). */
export function usePreviewAbsenceImpact(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (input: AbsenceInput): Promise<AbsenceImpact> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/absences/preview-impact', {
            params: { path: { personId } },
            body: input,
          }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people/{personId}/absences/preview-impact', {
            params: { path: { tenantId, personId } },
            body: input,
          }),
      );
      return required(data);
    },
    meta: { silent: true },
  });
}

/** `201` com `{ absence, impact }`: a prévia de impacto da ausência recém-criada. */
export function useCreateAbsence(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: AbsenceInput): Promise<AbsenceCreated> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/absences', {
            params: { path: { personId }, header: idempotency() },
            body: input,
          }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people/{personId}/absences', {
            params: { path: { tenantId, personId }, header: idempotency() },
            body: input,
          }),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface RemoveAbsenceArgs {
  readonly absenceId: string;
  /** Razão obrigatória (`422 PLT_CHANGE_REASON_REQUIRED`): a remoção é lógica e auditada. */
  readonly changeReason: ChangeReasonInput;
}

export function useRemoveAbsence(scope: PeopleScope, personId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: RemoveAbsenceArgs): Promise<Absence> => {
      const body = { changeReason: args.changeReason };
      const { data } = await viaScope(
        scope,
        () =>
          client.DELETE('/api/v1/network/people/{personId}/absences/{absenceId}', {
            params: { path: { personId, absenceId: args.absenceId } },
            body,
          }),
        (tenantId) =>
          client.DELETE('/api/v1/tenants/{tenantId}/people/{personId}/absences/{absenceId}', {
            params: { path: { tenantId, personId, absenceId: args.absenceId } },
            body,
          }),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Onboarding e offboarding por vínculo (REQ-PPL-013, 014) ----

/** `null` enquanto o onboarding do vínculo não foi iniciado (o servidor responde `404`). */
export function useOnboarding(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'links', linkId, 'onboarding'],
    queryFn: async (): Promise<OnboardingCase | null> => {
      try {
        const { data } = await viaScope(
          scope,
          () =>
            client.GET('/api/v1/network/people/{personId}/links/{linkId}/onboarding', {
              params: { path: { personId, linkId } },
            }),
          (tenantId) =>
            client.GET('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/onboarding', {
              params: { path: { tenantId, personId, linkId } },
            }),
        );
        return data ?? null;
      } catch (error: unknown) {
        if (isNotFound(error)) {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(personId) && Boolean(linkId),
    meta: { silent: true },
  });
}

/** Gera o checklist da função do vínculo (`409 PPL_ONBOARDING_ALREADY_STARTED` se já existe). */
export function useStartOnboarding(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (): Promise<OnboardingCase> => {
      const { data } = await viaScope(
        scope,
        () =>
          client.POST('/api/v1/network/people/{personId}/links/{linkId}/onboarding', {
            params: { path: { personId, linkId }, header: idempotency() },
          }),
        (tenantId) =>
          client.POST('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/onboarding', {
            params: { path: { tenantId, personId, linkId }, header: idempotency() },
          }),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface MarkOnboardingItemArgs {
  readonly itemCode: string;
  readonly notApplicable?: boolean | undefined;
  readonly evidenceDocumentId?: string | null | undefined;
}

export function useMarkOnboardingItem(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: MarkOnboardingItemArgs): Promise<OnboardingCase> => {
      const body = {
        notApplicable: args.notApplicable ?? false,
        evidenceDocumentId: args.evidenceDocumentId ?? null,
      };
      const { data } = await viaScope(
        scope,
        () =>
          client.PUT(
            '/api/v1/network/people/{personId}/links/{linkId}/onboarding/items/{itemCode}',
            { params: { path: { personId, linkId, itemCode: args.itemCode } }, body },
          ),
        (tenantId) =>
          client.PUT(
            '/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/onboarding/items/{itemCode}',
            { params: { path: { tenantId, personId, linkId, itemCode: args.itemCode } }, body },
          ),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

/** `null` enquanto o vínculo está ativo (o caso nasce sozinho ao encerrá-lo; o servidor responde `404`). */
export function useOffboarding(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'person', personId, 'links', linkId, 'offboarding'],
    queryFn: async (): Promise<OffboardingCase | null> => {
      try {
        const { data } = await viaScope(
          scope,
          () =>
            client.GET('/api/v1/network/people/{personId}/links/{linkId}/offboarding', {
              params: { path: { personId, linkId } },
            }),
          (tenantId) =>
            client.GET('/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/offboarding', {
              params: { path: { tenantId, personId, linkId } },
            }),
        );
        return data ?? null;
      } catch (error: unknown) {
        if (isNotFound(error)) {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(personId) && Boolean(linkId),
    meta: { silent: true },
  });
}

export interface MarkOffboardingItemArgs {
  readonly itemCode: string;
  readonly evidenceDocumentId?: string | null | undefined;
}

export function useMarkOffboardingItem(scope: PeopleScope, personId: string, linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: MarkOffboardingItemArgs): Promise<OffboardingCase> => {
      const body = { evidenceDocumentId: args.evidenceDocumentId ?? null };
      const { data } = await viaScope(
        scope,
        () =>
          client.PUT(
            '/api/v1/network/people/{personId}/links/{linkId}/offboarding/items/{itemCode}',
            { params: { path: { personId, linkId, itemCode: args.itemCode } }, body },
          ),
        (tenantId) =>
          client.PUT(
            '/api/v1/tenants/{tenantId}/people/{personId}/links/{linkId}/offboarding/items/{itemCode}',
            { params: { path: { tenantId, personId, linkId, itemCode: args.itemCode } }, body },
          ),
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Importação em lote (REQ-PPL-019; só tenant; `person.import` com step-up) ----

export function useImportBatches(tenantId: string, page = 1, pageSize = 20) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...tenantKey(tenantId), 'imports', page, pageSize],
    queryFn: async (): Promise<ImportBatchPage> => {
      const { data } = await client.GET('/api/v1/tenants/{tenantId}/people/imports', {
        params: { path: { tenantId }, query: { page, pageSize } },
      });
      return required(data);
    },
    enabled: Boolean(tenantId),
    meta: { silent: true },
  });
}

/** Detalhe com o relatório por linha (`rows[]`: linha, desfecho, ids, coluna e código — nunca valores). */
export function useImportBatch(tenantId: string, batchId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...tenantKey(tenantId), 'imports', batchId],
    queryFn: async (): Promise<ImportBatch> => {
      const { data } = await client.GET('/api/v1/tenants/{tenantId}/people/imports/{batchId}', {
        params: { path: { tenantId, batchId } },
      });
      return required(data);
    },
    enabled: Boolean(tenantId) && Boolean(batchId),
    meta: { silent: true },
  });
}

/** CSV no corpo JSON `{ fileName, mode, content }`; `replayed=true` quando o mesmo conteúdo já foi aplicado. */
export function useImportPeople(tenantId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: ImportPeopleInput): Promise<ImportBatch> => {
      const { data } = await client.POST('/api/v1/tenants/{tenantId}/people/imports', {
        params: { path: { tenantId }, header: idempotency() },
        body: input,
      });
      return required(data);
    },
    onSuccess: invalidate,
    // As variáveis levam o texto integral do CSV (CPF e nascimento): a mutação não fica no cache além do uso e a tela chama
    // `reset()` assim que recebe o lote.
    gcTime: 0,
    meta: { silent: true },
  });
}

// ---- Point-in-time (REQ-PPL-020; só rede; `people.history.read` + propósito) ----

export interface PointInTimeParams {
  readonly date: string;
  readonly tenantId?: string | undefined;
  readonly cursor?: string | undefined;
}

export function usePointInTime(params: PointInTimeParams | null, purpose: DeclaredPurpose | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'point-in-time', params, purpose?.code ?? null],
    queryFn: async (): Promise<PointInTimeReport> => {
      if (!params || !purpose) {
        throw new Error('point-in-time needs a date and a declared purpose');
      }
      const { data } = await client.GET('/api/v1/network/people/point-in-time', {
        params: {
          query: {
            date: params.date,
            ...(params.tenantId ? { tenantId: params.tenantId } : {}),
            ...(params.cursor ? { cursor: params.cursor } : {}),
            limit: 200,
          },
          header: purposeHeader(purpose),
        },
      });
      return required(data);
    },
    enabled: params !== null && purpose !== null,
    gcTime: 0,
    meta: { silent: true },
  });
}

// ---- "Meu cadastro" (SelfScope: `/me/person*`, `person.self` implícita; a pessoa vem da claim `pid`) ----

export function useMyPerson() {
  const client = useApiClient();
  return useQuery({
    queryKey: [...meKey, 'person'],
    queryFn: async (): Promise<Versioned<PersonDetail>> => {
      const { data, response } = await client.GET('/api/v1/me/person');
      return { data: required(data), etag: etagOf(response) };
    },
    meta: { silent: true },
  });
}

/** Os próprios vínculos em toda pessoa jurídica (um cartão por PJ), sem papel de rede. */
export function useMyLinks() {
  const client = useApiClient();
  return useQuery({
    queryKey: [...meKey, 'links'],
    queryFn: async (): Promise<PersonLink[]> => {
      const { data } = await client.GET('/api/v1/me/person/links');
      return data ?? [];
    },
    meta: { silent: true },
  });
}

export function useMyCertifications() {
  const client = useApiClient();
  return useQuery({
    queryKey: [...meKey, 'certifications'],
    queryFn: async (): Promise<Certification[]> => {
      const { data } = await client.GET('/api/v1/me/person/certifications');
      return data ?? [];
    },
    meta: { silent: true },
  });
}

/** Contatos da própria pessoa (razão fixa `PPL_SELF_CONTACTS`): o conjunto inteiro é a unidade. */
export function useUpdateMyContacts() {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (contacts: readonly ContactInput[]): Promise<PersonDetail> => {
      const { data } = await client.PUT('/api/v1/me/person/contacts', {
        body: { contacts: [...contacts] },
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useMyAvailability(linkId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...meKey, 'links', linkId, 'availability'],
    queryFn: async (): Promise<Availability[]> => {
      const { data } = await client.GET('/api/v1/me/person/links/{linkId}/availability', {
        params: { path: { linkId } },
      });
      return data ?? [];
    },
    enabled: Boolean(linkId),
    meta: { silent: true },
  });
}

/** Razão fixa `PPL_SELF_AVAILABILITY`; o `201` devolve o `ETag` da declaração (o `PUT` precisa dele). */
/** Uma declaração da própria pessoa com o `ETag` (OPEN-096): relida a cada abertura do diálogo de edição, para a edição valer em qualquer sessão. */
export function useMyAvailabilityDeclaration(linkId: string, declarationId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...meKey, 'links', linkId, 'availability', declarationId],
    queryFn: async (): Promise<Versioned<Availability>> => {
      const { data, response } = await client.GET(
        '/api/v1/me/person/links/{linkId}/availability/{declarationId}',
        { params: { path: { linkId, declarationId } } },
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(linkId) && Boolean(declarationId),
    refetchOnMount: 'always',
    meta: { silent: true },
  });
}

export function useCreateMyAvailability(linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: AvailabilityInput): Promise<Versioned<Availability>> => {
      const { data, response } = await client.POST(
        '/api/v1/me/person/links/{linkId}/availability',
        { params: { path: { linkId }, header: idempotency() }, body: input },
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useUpdateMyAvailability(linkId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: UpdateAvailabilityArgs): Promise<Versioned<Availability>> => {
      const { data, response } = await client.PUT(
        '/api/v1/me/person/links/{linkId}/availability/{declarationId}',
        {
          params: {
            path: { linkId, declarationId: args.declarationId },
            header: ifMatch(args.etag),
          },
          body: args.input,
        },
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useMyAbsences(range: AbsenceRange = {}) {
  const client = useApiClient();
  const query = absenceQuery(range);
  return useQuery({
    queryKey: [...meKey, 'absences', range],
    queryFn: async (): Promise<Absence[]> => {
      const { data } = await client.GET('/api/v1/me/person/absences', { params: { query } });
      return data ?? [];
    },
    meta: { silent: true },
  });
}

export function usePreviewMyAbsenceImpact() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (input: AbsenceInput): Promise<AbsenceImpact> => {
      const { data } = await client.POST('/api/v1/me/person/absences/preview-impact', {
        body: input,
      });
      return required(data);
    },
    meta: { silent: true },
  });
}

export function useCreateMyAbsence() {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: AbsenceInput): Promise<AbsenceCreated> => {
      const { data } = await client.POST('/api/v1/me/person/absences', {
        params: { header: idempotency() },
        body: input,
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface RemoveMyAbsenceArgs {
  readonly absenceId: string;
  /** Opcional: sem razão, vale a fixa `PPL_SELF_ABSENCE`. */
  readonly changeReason?: ChangeReasonInput | undefined;
}

export function useRemoveMyAbsence() {
  const client = useApiClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: RemoveMyAbsenceArgs): Promise<Absence> => {
      const { data } = await client.DELETE('/api/v1/me/person/absences/{absenceId}', {
        params: { path: { absenceId: args.absenceId } },
        body: args.changeReason ? { changeReason: args.changeReason } : null,
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}
